<template>
  <div class="mb-4 flex flex-col items-start">
    <p class="mb-2 text-xl text-gpx_black-200">Send notifications when</p>
    <p v-if="isB2B && isAdminRole" class="text-gpx_black-200">
      Device Notifications
    </p>
    <VCheckbox v-model="form.notify_geofence_enter_exit">
      <span class="flex-1">Device enters/exits a geofence</span>
    </VCheckbox>
    <VCheckbox v-model="form.notify_low_charge">
      <span class="flex-1">Device has a low battery</span>
    </VCheckbox>
    <VCheckbox v-model="form.notify_device_alert">
      <span class="flex-1">
        Device sends tamper, temperature, movement, etc. alert
        <i>(supported alerts depend on hardware)</i>
      </span>
    </VCheckbox>
    <VCheckbox v-model="form.notify_shipment_begin_end_late">
      <span class="flex-1">
        Shipment lifecycle, delays, and exceptions (excluding route deviation)
      </span>
    </VCheckbox>

    <template v-if="isB2B && isAdminRole">
      <p class="mt-2 text-gpx_black-200">Financial Notifications</p>
      <VCheckbox v-model="form.receive_subscription_notifications">
        <span class="flex-1">Subscription Notifications</span>
      </VCheckbox>
      <VCheckbox v-model="form.receive_billing_notifications">
        <span class="flex-1">Order and Billing Notifications</span>
      </VCheckbox>
    </template>
  </div>
  <div v-if="isGroupFilterVisible" class="mb-4 flex flex-col items-start">
    <p class="mb-2 text-xl text-gpx_black-200">Send notifications from</p>
    <VCheckbox v-model="limitToGroups">
      <span class="flex-1">Only certain tracker groups</span>
    </VCheckbox>
    <div v-if="limitToGroups" class="flex flex-col pl-[22px]">
      <VCheckbox v-model="groupFilter.ungrouped">
        <span class="flex-1">(ungrouped)</span>
      </VCheckbox>
      <VCheckbox
        v-for="group in groups"
        :key="group.id"
        :model-value="groupFilter.groups.includes(group.id)"
        @update:model-value="toggleGroup(group.id, $event)"
      >
        <span class="flex-1">{{ group.name }}</span>
      </VCheckbox>
    </div>
  </div>
  <div class="mb-4 flex flex-col items-start">
    <p class="mb-2 text-xl text-gpx_black-200">Send notifications via</p>
    <VCheckbox v-model="form.notify_via_email">
      <span class="flex-1">Email to {{ user.email }}</span>
    </VCheckbox>
    <VCheckbox v-model="form.notify_via_mobile">
      <span class="flex-1">Push notifications on mobile application</span>
    </VCheckbox>
    <div v-if="user.phone">
      <VCheckbox v-model="form.notify_via_sms">
        <span class="flex-1">SMS to {{ user.phone }}</span>
      </VCheckbox>
      <div class="mb-3 block pl-[22px]">
        <small>
          <p>You agree to receive SMS notifications from GPX.</p>
          <p>
            GPX SMS messages may include low battery alerts, geofence entry or
            exit notifications, or user-configured alerts created via the Rules
            Engine. Message and data rates may apply. One message is sent per
            notification. Message frequency varies
          </p>
          <p>Reply HELP for help; STOP to opt out.</p>
          <a
            target="_blank"
            href="https://logistimatics.com/terms-of-service/"
            class="text-gpx_terracotta-200 hover:text-gpx_red-200 hover:underline"
            >Terms of Service</a
          >
          and
          <a
            target="_blank"
            href="https://logistimatics.com/privacy/"
            class="text-gpx_terracotta-200 hover:text-gpx_red-200 hover:underline"
            >Privacy Policy</a
          >.
        </small>
      </div>
    </div>
  </div>
  <div>
    <Button
      type="submit"
      class="w-48"
      @click="handleSubmit"
      :loading="isSubmitting"
      loadingText="Updating..."
      :disabled="isSubmitting"
    >
      Update notifications
    </Button>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import each from 'lodash/each';
import map from 'lodash/map';
import get from 'lodash/get';
import { Button } from '@gpx-spa/ui';
import {
  NOTIFY_GROUPS_CODE,
  notifyGroupsSelection,
  parseNotifyGroups,
  serializeNotifyGroups,
} from '@gpx-spa/constants';
import VCheckbox from '@/components/ui/VCheckbox.vue';
import service from '@/services';
import { useToast } from 'vue-toastification';
import { COMMON_UPDATE_SUCCESS } from '@/config/constants';

export default {
  components: {
    Button,
    VCheckbox,
  },

  data() {
    return {
      isSubmitting: false,
      groups: [],
      groupFilter: { all: true, groups: [], ungrouped: true },
      form: {
        notify_geofence_enter_exit: false,
        notify_low_charge: false,
        notify_device_alert: false,
        notify_shipment_begin_end_late: false,
        receive_subscription_notifications: true,
        receive_billing_notifications: true,
        notify_via_email: false,
        notify_via_mobile: false,
        notify_via_sms: false,
      },
    };
  },

  mounted() {
    // `notify_groups` holds JSON, not a scalar boolean, so it stays out of this loop.
    each(this.form, (defaultValue, code) => {
      this.form[code] = Boolean(+get(this.userSetting, code, defaultValue));
    });
    this.loadGroupFilter();
  },

  methods: {
    ...mapActions({
      fetchSettingsByAccountId: 'settings/fetchByAccountId',
      storeSettingsUnderAccountId: 'settings/storeUnderAccountId',
    }),
    loadGroupFilter() {
      return service
        .get(`/accounts/${this.accountId}/groups`, {
          params: { exclude_devices: 1 },
        })
        .then((res) => {
          this.groups = res.data.data;

          const stored = parseNotifyGroups(
            this.userSetting[NOTIFY_GROUPS_CODE],
          );
          this.groupFilter = {
            all: stored.all,
            groups: notifyGroupsSelection(stored, this.groupIds),
            ungrouped: stored.ungrouped,
          };
        })
        .catch(() => {
          // Without a group list there is nothing to choose from — the control
          // stays hidden and the rest of the form still saves.
        });
    },
    toggleGroup(id, isSelected) {
      this.groupFilter.groups = isSelected
        ? [...this.groupFilter.groups, id]
        : this.groupFilter.groups.filter((selected) => selected !== id);
    },
    handleSubmit() {
      const toast = useToast();

      this.isSubmitting = true;
      this.storeSettingsUnderAccountId({
        accountId: this.accountId,
        params: {
          settings: this.formattedSettings,
        },
      })
        .then(() => {
          toast.success(COMMON_UPDATE_SUCCESS);
          this.isSubmitting = false;
        })
        .catch(() => {
          this.isSubmitting = false;
        });
    },
  },

  computed: {
    ...mapGetters({
      user: 'auth/user',
      accountId: 'auth/accountId',
      userSetting: 'settings/user',
      isB2B: 'auth/isB2B',
      isAdminRole: 'auth/isAdminRole',
    }),
    groupIds() {
      return map(this.groups, 'id');
    },
    // The stored flag is "all groups"; the checkbox offers the opposite, so an
    // untouched form reads as everything without anyone having to tick a box.
    limitToGroups: {
      get() {
        return !this.groupFilter.all;
      },
      set(value) {
        this.groupFilter.all = !value;
      },
    },
    isAnyAlertTypeOn() {
      return (
        this.form.notify_geofence_enter_exit ||
        this.form.notify_low_charge ||
        this.form.notify_device_alert ||
        this.form.notify_shipment_begin_end_late
      );
    },
    // Below two visible groups there is nothing to choose, so the control
    // collapses to today's behaviour and is not rendered at all.
    isGroupFilterVisible() {
      return this.groups.length > 1 && this.isAnyAlertTypeOn;
    },
    formattedSettings() {
      const settings = map(this.form, (value, code) => {
        return { code, value: Boolean(+value) };
      });

      if (this.groups.length > 1) {
        settings.push({
          code: NOTIFY_GROUPS_CODE,
          value: serializeNotifyGroups(this.groupFilter, this.groupIds),
        });
      }

      return settings;
    },
  },
  watch: {
    accountId(accountId) {
      this.fetchSettingsByAccountId({ accountId });
    },
  },
};
</script>

<style></style>
